import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from './api';
import { useAuth } from '@/lib/auth/useAuth';
import { classifyLoginError } from './loginError';
import { takePendingConsent } from '@/features/oauth/pendingConsent';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { Eyebrow } from '@/lib/ui/Typography';
import { Wordmark } from '@/lib/ui/Wordmark';

/**
 * Login + conditional MFA (spec §3.1). The TOTP field appears once the server says the
 * account needs one; lockout and rate-limit get specific messages. Deep-link preserved.
 *
 * The mapping from a failed login to what the user is told lives in `classifyLoginError`
 * — it is the security-visible part of this screen and it is unit-tested there.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { setSession, pendingDeepLink } = useAuth();
  const navigate = useNavigate();
  // The reset page lands here after a new password is set.
  const justReset = (useLocation().state as { notice?: string } | null)?.notice === 'passwordReset';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.login({
        email: email.trim().toLowerCase(),
        password,
        totp_code: needsTotp ? totp : undefined,
      });
      await setSession(tokens);
      // No 2FA on the account yet: the session is good for enrolment only, and
      // a pending consent waits in storage until the setup screen is done.
      if (tokens.mfa_enrol) {
        navigate('/account/2fa-setup', { replace: true });
        return;
      }
      // A pending OAuth consent outranks the in-memory deep link: it is the one
      // destination that survives a reload of this page, and an AI app is
      // waiting on it (see features/oauth/pendingConsent).
      navigate(takePendingConsent() ?? pendingDeepLink ?? '/');
    } catch (err) {
      const { message, needsTotp: reveal } = classifyLoginError(err, needsTotp);
      setError(t(message));
      setNeedsTotp(reveal);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} className="flex flex-col gap-md">
        <div>
          <Eyebrow>§ {t('auth.eyebrowSignIn')}</Eyebrow>
          <h1 className="mt-2xs text-headline">{t('auth.signIn')}</h1>
        </div>
        {justReset && !error && (
          <p className="text-small text-signal-teal" role="status">
            {t('auth.passwordResetDone')}
          </p>
        )}
        <TextInput
          label={t('auth.email')}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextInput
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {needsTotp && (
          <TextInput
            label={t('auth.totp')}
            inputMode="numeric"
            autoComplete="one-time-code"
            hint={t('auth.totpHint')}
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            autoFocus
          />
        )}
        {error && (
          <p className="text-small text-signal-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" loading={loading}>
          {t('auth.signIn')}
        </Button>
        {/* `self-start py-2xs`: a bare link in a flex column stretches to the
            full width and is exactly one line tall — 350×20 in the built
            console, under the 24px target minimum, and a 350px-wide target for
            a 14-character link. Now it is as wide as its words and 28px tall. */}
        <Link
          to="/login/forgot"
          className="self-start rounded-sm py-2xs text-small text-accent-blue hover:text-ink-deep focus-visible:shadow-focus"
        >
          {t('auth.forgot')}
        </Link>
      </form>
    </AuthLayout>
  );
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-lg bg-ink-navy p-md sm:p-lg">
      {/* The wordmark sits ABOVE the card, on the ink ground, because this is the
          one screen whose whole job is to say whose console this is. `p-md` at
          the narrow end: 24px gutters plus a 48px card inset left a 320px phone
          about 176px of usable width. */}
      <Wordmark size={40} withText={false} />
      <div className="w-full max-w-md rounded-lg border-[0.5px] border-hairline bg-canvas-paper p-lg sm:p-2xl">
        {children}
      </div>
      <p className="text-small text-canvas-bone">{t('app.title')}</p>
    </div>
  );
}
